import {
  type DateValue,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import { Calendar } from "@untitledui/icons";
import type React from "react";
import { useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import { Button as AriaButton, useLocale } from "react-aria-components";
import {
  DateRangeCalendarPopover,
  type DateRangePreset,
} from "@/components/application/date-picker/date-range-calendar-popover";
import { cx } from "@/utils/cx";
import { generateDateRangePresets } from "@/utils/datePresets";

interface CustomReportDateRangeSelectorProps {
  timePeriod: "lastMonth" | "lastWeek" | "specificRange";
  onTimePeriodChange: (period: "lastMonth" | "lastWeek" | "specificRange") => void;
  dateRange: { start: DateValue; end: DateValue } | null;
  onDateRangeChange: (range: { start: DateValue; end: DateValue } | null) => void;
  limits?: { minDate: DateValue; maxDate: DateValue };
}

export const CustomReportDateRangeSelector: React.FC<CustomReportDateRangeSelectorProps> = ({
  timePeriod,
  onTimePeriodChange,
  dateRange,
  onDateRangeChange,
  limits,
}) => {
  // Local state for the DateRangePicker (used when "Specific range" is selected)
  const [tempDateRange, setTempDateRange] = useState<{ start: DateValue; end: DateValue } | null>(dateRange);
  const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>(null);

  // Get locale and formatter for preset generation
  const { locale } = useLocale();
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Generate date range presets
  const presets: Record<string, DateRangePreset> = useMemo(() => {
    return generateDateRangePresets(locale, limits, formatter);
  }, [locale, limits, formatter]);

  // Handle preset selection from sidebar
  const handlePresetSelect = (key: string, preset: DateRangePreset) => {
    setTempDateRange(preset.value);
    setSelectedPresetKey(key);
  };

  // Calculate date range based on time period selection
  const calculateDateRange = (type: "lastMonth" | "lastWeek"): { start: DateValue; end: DateValue } => {
    const now = today(getLocalTimeZone());
    if (type === "lastMonth") {
      const lastMonthStart = startOfMonth(now).subtract({ months: 1 });
      const lastMonthEnd = endOfMonth(now).subtract({ months: 1 });
      return { start: lastMonthStart, end: lastMonthEnd };
    }
    // lastWeek
    const lastWeekStart = startOfWeek(now, "en-US").subtract({ weeks: 1 });
    const lastWeekEnd = endOfWeek(now, "en-US").subtract({ weeks: 1 });
    return { start: lastWeekStart, end: lastWeekEnd };
  };

  // Check if a preset is available (within date limits)
  const isPresetAvailable = (type: "lastMonth" | "lastWeek"): boolean => {
    if (!limits) return true;
    const range = calculateDateRange(type);
    return range.start.compare(limits.minDate) >= 0 && range.end.compare(limits.maxDate) <= 0;
  };

  // Handle preset button clicks
  const handlePresetClick = (preset: "lastMonth" | "lastWeek") => {
    if (!isPresetAvailable(preset)) return; // Don't allow if outside date limits
    onTimePeriodChange(preset);
    const range = calculateDateRange(preset);
    onDateRangeChange(range);
    setTempDateRange(range);
  };

  return (
    <div className="flex gap-17.5 w-full">
      <div className="flex flex-col gap-1 w-[20%] shrink-0">
        <div className="flex gap-1.5 items-center">
          <Calendar className="size-5 text-tertiary" />
          <span className="text-sm font-medium text-secondary">Time Period</span>
        </div>
        <span className="text-xs text-quaternary">The time frame for your report</span>
      </div>

      <div className="flex flex-col gap-2 flex-1 min-w-0 w-[80%]">
        <div className="p-1 bg-primary rounded-lg outline outline-1 outline-offset-[-1px] outline-primary inline-flex justify-start items-start gap-1">
          <button
            type="button"
            onClick={() => handlePresetClick("lastMonth")}
            disabled={!isPresetAvailable("lastMonth")}
            className={
              timePeriod === "lastMonth"
                ? "flex-1 h-8 px-2 py-2 bg-secondary rounded-lg outline outline-1 outline-offset-[-1px] outline-fg-primary flex justify-center items-center overflow-hidden whitespace-nowrap"
                : "flex-1 h-8 px-2 py-2 flex justify-center items-center overflow-hidden whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            }
          >
            <div className="text-primary text-sm font-medium leading-5 truncate">Last Month</div>
          </button>
          <button
            type="button"
            onClick={() => handlePresetClick("lastWeek")}
            disabled={!isPresetAvailable("lastWeek")}
            className={
              timePeriod === "lastWeek"
                ? "flex-1 h-8 px-2 py-2 bg-secondary rounded-lg outline outline-1 outline-offset-[-1px] outline-fg-primary flex justify-center items-center overflow-hidden whitespace-nowrap"
                : "flex-1 h-8 px-2 py-2 flex justify-center items-center overflow-hidden whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            }
          >
            <div className="text-primary text-sm font-medium leading-5 truncate">Last Week</div>
          </button>
          <div className="flex-1 min-w-0">
            <DateRangeCalendarPopover
              value={tempDateRange}
              onChange={(value) => {
                setTempDateRange(value);
                setSelectedPresetKey(null); // Clear preset when manually selecting dates
              }}
              onApply={() => {
                if (tempDateRange) {
                  onDateRangeChange(tempDateRange);
                  onTimePeriodChange("specificRange");
                }
              }}
              onReset={() => {
                const now = today(getLocalTimeZone());
                const thirtyOneDaysAgo = now.subtract({ days: 31 });
                const resetRange = { start: thirtyOneDaysAgo, end: now };
                setTempDateRange(resetRange);
                onDateRangeChange(resetRange);
                onTimePeriodChange("specificRange");
                setSelectedPresetKey(null);
              }}
              resetText="Last 31 days"
              minValue={limits?.minDate}
              maxValue={limits?.maxDate}
              placement="bottom right"
              showPresets={true}
              presets={presets}
              selectedPresetKey={selectedPresetKey}
              onPresetSelect={handlePresetSelect}
            >
              <AriaButton
                className={cx(
                  "w-full h-8 px-2 py-2 rounded-lg flex justify-center items-center overflow-hidden",
                  timePeriod === "specificRange"
                    ? "bg-secondary outline outline-1 outline-offset-[-1px] outline-fg-primary"
                    : "",
                )}
              >
                <div className="text-primary text-sm font-medium leading-5 truncate">
                  {timePeriod === "specificRange" && dateRange
                    ? `${dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                    : "Specific range"}
                </div>
              </AriaButton>
            </DateRangeCalendarPopover>
          </div>
        </div>
      </div>
    </div>
  );
};
